import { useCallback } from "react";

import { isEmptyArray } from "@/lib/chillnn/is-empty-array";

import { useCsv } from "@/hooks/use-csv";
import { CsvFileNamePrefixes, CsvHeaders } from "@/hooks/use-csv/constants";
import { escapeCsvQuestionHeader, quoteCsvField } from "@/hooks/use-csv/csv-field";

import { useHandleReservation } from "@/components/features/management/reservation/search/_shared";
import { filterOptionsByStayDate } from "@/components/features/management/reservation/search/_shared/filter-options-by-stay-date";

import type * as _Model from "../../../../../model";

type Props = {
	viewData: _Model.Type.ViewData;
};

export const useHandleCsvDownload = ({
	viewData: {
		csvDownloadData: { hotelName, hotelPrefecture, reservationList },
	},
}: Props) => {
	const { csvGenerate, csvDownload } = useCsv();
	const {
		// 宿泊券の合計金額を算出する
		onCalcTotalVoucherAmount,
		// ディスカウントの合計金額を算出する
		onCalcTotalDiscountAmount,
		// ディスカウント前のゲストが支払う金額を算出する
		onCalcPrevTotalGuestChargeAmount,
		// 通算での宿泊数を算出する
		onCalcTotalStayCount,
		// 宿泊券名をリストで返却する
		onGetVoucherNameList,
		// チェックアウト日を返却する
		onGetCheckOutDate,
		// 支払方法テキストを返却する
		onGetPaymentMethodText,
		// NO SHOW テキストを返却する
		onGetNoShowText,
		// プラン名をリストで返却する
		onGetPlanNameList,
		// オプション名をリストで返却する
		onGetOptionNameList,
		// 部屋名をリストで返却する
		onGetRoomNameList,
	} = useHandleReservation();

	/**
	 * @todo Question の追加
	 */
	const onCsvDownload = useCallback(() => {
		const csvHeaders = [...CsvHeaders.reservation.perDate];
		// 質問のヘッダーを追加
		for (const reservation of reservationList) {
			for (const question of reservation.questions) {
				const escapedQuestion = escapeCsvQuestionHeader(question.question);
				/**
				 * 重複していなければ
				 */
				if (!csvHeaders.includes(escapedQuestion)) csvHeaders.push(escapedQuestion);
			}
		}

		// 宿泊日ごとに変換
		const _reservationList: _Model.Type.ViewData["csvDownloadData"]["reservationList"] = [];
		for (const reservation of reservationList) {
			// キャンセル済み予約の場合、roomは空でroomCanceledにデータがある
			const roomsToProcess = isEmptyArray(reservation.room) ? reservation.roomCanceled : reservation.room;
			for (const room of roomsToProcess) {
				_reservationList.push({ ...reservation, room: [room] });
			}
		}
		// 昇順にソート
		const sortedReservationList = _reservationList.sort((a, b) => (a.checkIn < b.checkIn ? -1 : 1));

		const rows = sortedReservationList.map((reservation) => {
			const csvRows: Array<string | number> = Array.from({ length: csvHeaders.length }, () => "");

			csvRows[csvHeaders.indexOf("予約サイト名")] = "CHILLNN";
			/**
			 * その日の部屋の日付を宿泊日と捉える
			 */
			csvRows[csvHeaders.indexOf("宿泊日")] = reservation.room[0].date;
			csvRows[csvHeaders.indexOf("予約ID")] = reservation.reservationID;
			csvRows[csvHeaders.indexOf("予約成立日")] = reservation.reserveDate;
			csvRows[csvHeaders.indexOf("最終予約更新日")] = reservation.latestUpdateDate;
			csvRows[csvHeaders.indexOf("チェックイン日")] = reservation.checkIn;
			const { checkOutDate } = onGetCheckOutDate({
				checkIn: reservation.checkIn,
				reservationType: reservation.reservationType,
				checkOut: reservation.checkOut,
			});
			csvRows[csvHeaders.indexOf("チェックアウト日")] = checkOutDate;
			const { totalStayCount } = onCalcTotalStayCount({
				checkIn: reservation.checkIn,
				checkOut: checkOutDate,
			});
			csvRows[csvHeaders.indexOf("泊数")] = totalStayCount;
			csvRows[csvHeaders.indexOf("宿泊者氏名")] = reservation.guestName;
			csvRows[csvHeaders.indexOf("宿泊者氏名（カナ）")] = reservation.guestNameKana ?? "";
			csvRows[csvHeaders.indexOf("部屋マスタ")] = reservation.room
				.map(({ name, date }) => `【${date}】 ${name} `)
				.join(`\n`);
			const { roomNameList: canceledRoomNameList } = onGetRoomNameList({ roomList: reservation.roomCanceled });
			csvRows[csvHeaders.indexOf("キャンセル済み部屋マスタ")] = canceledRoomNameList.join(`\n`);
			csvRows[csvHeaders.indexOf("プラン名称")] = reservation.plan ?? "";
			const { planNameList: canceledPlanNameList } = onGetPlanNameList({ planList: reservation.planCanceled });
			csvRows[csvHeaders.indexOf("キャンセル済みプラン名称")] = canceledPlanNameList.join(`\n`);
			csvRows[csvHeaders.indexOf("部屋数")] = reservation.numOfRoom;
			csvRows[csvHeaders.indexOf("大人人数")] = reservation.numOfAdult;
			csvRows[csvHeaders.indexOf("子供人数")] = reservation.numOfKids;
			const { voucherNameList } = onGetVoucherNameList({ voucherList: reservation.vouchers });
			csvRows[csvHeaders.indexOf("ホテルチケット")] = voucherNameList.join("\n");
			const { totalVoucherAmount } = onCalcTotalVoucherAmount({ voucherList: reservation.vouchers });
			const { totalDiscountAmount } = onCalcTotalDiscountAmount({
				discountAmount: reservation.priceDiscount,
				totalVoucherAmount: totalVoucherAmount,
			});
			const { prevTotalGuestChargeAmount } = onCalcPrevTotalGuestChargeAmount({
				totalGuestChargeAmount: reservation.totalGuestCharge,
				totalDiscountAmount: totalDiscountAmount,
			});
			csvRows[csvHeaders.indexOf("ディスカウント前金額")] = prevTotalGuestChargeAmount;
			csvRows[csvHeaders.indexOf("ディスカウント")] = reservation.priceDiscount;
			csvRows[csvHeaders.indexOf("プラン売上金額")] = reservation.pricePlanTotal;
			csvRows[csvHeaders.indexOf("お部屋売上金額")] = reservation.priceRoomTotal;
			csvRows[csvHeaders.indexOf("オプション売上金額")] = reservation.priceOptionTotal;
			csvRows[csvHeaders.indexOf("全ての宿泊予約に対して課する料金合計")] = reservation.priceAdditionalFeeTotal;
			csvRows[csvHeaders.indexOf("請求料金")] = reservation.totalGuestCharge;
			const { paymentMethodText } = onGetPaymentMethodText({ paymentMethod: reservation.paymentMethod });
			csvRows[csvHeaders.indexOf("支払方法")] = paymentMethodText;
			const stayDate = reservation.room[0].date;
			const { options, canceledOptions } = filterOptionsByStayDate({ reservation, stayDate });
			const { optionNameList } = onGetOptionNameList({ optionList: options });
			csvRows[csvHeaders.indexOf("オプション")] = optionNameList.join(`\n`);
			const { optionNameList: canceledOptionNameList } = onGetOptionNameList({ optionList: canceledOptions });
			csvRows[csvHeaders.indexOf("キャンセル済みオプション")] = canceledOptionNameList.join(`\n`);
			csvRows[csvHeaders.indexOf("チェックイン時刻")] = reservation.checkInTimeStart;
			csvRows[csvHeaders.indexOf("メールアドレス")] = reservation.guestEmail;
			csvRows[csvHeaders.indexOf("郵便番号")] = reservation.guestPostalCode;
			csvRows[csvHeaders.indexOf("住所")] = reservation.guestAddress;
			csvRows[csvHeaders.indexOf("電話番号")] = reservation.guestTel;
			csvRows[csvHeaders.indexOf("キャンセル日")] = reservation.canceledDate ?? "";
			csvRows[csvHeaders.indexOf("備考")] = ""; // 常にブランク
			const { noShowText } = onGetNoShowText({ noShow: reservation.isNoShow });
			csvRows[csvHeaders.indexOf("NO SHOW")] = noShowText;
			csvRows[csvHeaders.indexOf("スタッフメモ")] = reservation.memoByHost ?? "";
			csvRows[csvHeaders.indexOf("ホテル名")] = hotelName;
			csvRows[csvHeaders.indexOf("ホテル所在地")] = hotelPrefecture;
			csvRows[csvHeaders.indexOf("作成日時")] = reservation.createdAt;
			for (const question of reservation.questions) {
				const escapedQuestion = escapeCsvQuestionHeader(question.question);
				const header = csvHeaders.indexOf(escapedQuestion);
				if (header !== -1) {
					csvRows[header] = question.answer.map((answer) => answer).join(", ");
				}
			}

			return csvRows.map(quoteCsvField).join(",");
		});

		const csv = csvGenerate(csvHeaders, rows);
		csvDownload(CsvFileNamePrefixes.reservation, csv);
	}, [
		csvDownload,
		csvGenerate,
		hotelName,
		hotelPrefecture,
		onCalcPrevTotalGuestChargeAmount,
		onCalcTotalDiscountAmount,
		onCalcTotalStayCount,
		onCalcTotalVoucherAmount,
		onGetCheckOutDate,
		onGetNoShowText,
		onGetOptionNameList,
		onGetPaymentMethodText,
		onGetPlanNameList,
		onGetRoomNameList,
		onGetVoucherNameList,
		reservationList,
	]);

	return {
		onCsvDownload,
	};
};
